import cls from 'classnames';
import * as React from 'react';
import { ProFilter } from 'react-pro-components';
import { Form, Table } from 'ssc-ui-react';
import { SorterResult } from 'ssc-ui-react/es/components/table/types';
import { ColumnProps } from 'ssc-ui-react/typings/components/table/types';

import { ReactComponent as ExceptionIcon } from '@/assets/svg/exception.svg';
import { $gt } from '@/common/appCtx';
import { CacheType, DEFAULT_PAGE_SIZE } from '@/pages/workstation-dashboard/const';
import { OperatorOptionItem, OperatorPerformanceLastTenHoursItem, WorkstationOptionItem } from '@/pages/workstation-dashboard/defined';
import useDropdownListStore from '@/pages/workstation-dashboard/store/use-dropdown-list';
import useLastTenHoursOperatorPerformanceStore, { defaultSortModel } from '@/pages/workstation-dashboard/store/use-last-ten-hours-operator-performance';
import useWorkstationOverviewStore from '@/pages/workstation-dashboard/store/use-workstation-overview';
import { extractHourAndSuffix, getStartHourBySecond, getWorkstationDashboardCache } from '@/pages/workstation-dashboard/utils/common';
import { dashboardDBManager } from '@/pages/workstation-dashboard/utils/storage';

import styles from '../index.module.less';

type FormType = {
  biz_workstation_id_list?: string[];
  operator_id_list?: string[];
};

type StorageType = {
  biz_workstation_list?: WorkstationOptionItem[];
  operator_list?: OperatorOptionItem[];
};

const OperatorPerformanceLastTenHours: React.FC = () => {
  const [form] = Form.useForm();
  const sorterModelRef = React.useRef(defaultSortModel);
  const targetGroup = useWorkstationOverviewStore.use.targetGroup();
  const workstationDropdownList = useDropdownListStore.use.lastTenHoursWorkstationDropdownList();
  const workstationDropdownListMap = useDropdownListStore.use.lastTenHoursWorkstationDropdownListMap();
  const workstationDropdownListLoading = useDropdownListStore.use.lastTenHoursWorkstationDropdownListLoading();
  const setWorkstationDropdownList = useDropdownListStore.use.setLastTenHoursWorkstationDropdownList();
  const operatorDropdownList = useDropdownListStore.use.lastTenHoursOperatorDropdownList();
  const operatorDropdownListMap = useDropdownListStore.use.lastTenHoursOperatorDropdownListMap();
  const operatorDropdownListLoading = useDropdownListStore.use.lastTenHoursOperatorDropdownListLoading();
  const setOperatorDropdownList = useDropdownListStore.use.setLastTenHoursOperatorDropdownList();
  const sorterModel = useLastTenHoursOperatorPerformanceStore.use.sorterModel();
  const pagination = useLastTenHoursOperatorPerformanceStore.use.pagination();
  const setPagination = useLastTenHoursOperatorPerformanceStore.use.setPagination();
  const setSorterModel = useLastTenHoursOperatorPerformanceStore.use.setSorterModel();
  const loading = useLastTenHoursOperatorPerformanceStore.use.loading();
  const getOperatorPerformanceLastTenHoursAsync = useLastTenHoursOperatorPerformanceStore.use.getOperatorPerformanceLastTenHoursAsync();
  const items = useLastTenHoursOperatorPerformanceStore.use.items();
  const setForm = useLastTenHoursOperatorPerformanceStore.use.setForm();
  const timeIntervals = useWorkstationOverviewStore.use.timeIntervals();

  const setCache = React.useCallback((values: FormType) => {
    const cache: StorageType = {
      biz_workstation_list: values.biz_workstation_id_list?.map(value => ({
        value,
        label: workstationDropdownListMap[value] || '',
      })),
      operator_list: values.operator_id_list?.map(value => ({
        value,
        label: operatorDropdownListMap[value] || '',
      })),
    };
    dashboardDBManager.put(CacheType.OperatorPerformanceLast10Hours, cache);
  }, [operatorDropdownListMap, workstationDropdownListMap]);

  const getCache = React.useCallback(async () => {
    try {
      const cache = await dashboardDBManager.get(CacheType.OperatorPerformanceLast10Hours) as StorageType | undefined;
      const workstationList = cache?.biz_workstation_list;
      const operatorList = cache?.operator_list;
      const bizWorkstationIdList: string[] = [];
      const disabledWorkstationList: WorkstationOptionItem[] = [];
      workstationList?.forEach(item => {
        if (!workstationDropdownListMap[item.value]) {
          disabledWorkstationList.push({ ...item, disabled: true });
        }
        bizWorkstationIdList.push(item.value);
      });
      setWorkstationDropdownList(disabledWorkstationList, true);
      form.setFieldValue('biz_workstation_id_list', bizWorkstationIdList.length ? bizWorkstationIdList : undefined);
      const disabledOperatorList: OperatorOptionItem[] = [];
      const operatorIdList: string[] = [];
      operatorList?.forEach(item => {
        if (!operatorDropdownListMap[item.value]) {
          disabledOperatorList.push({ ...item, disabled: true });
        }
        operatorIdList.push(item.value);
      });
      setOperatorDropdownList(disabledOperatorList, true);
      form.setFieldValue('operator_id_list', operatorIdList.length ? operatorIdList : undefined);
    } catch (error) {
      console.error('[OperatorPerformanceLastTenHours] getCache error: ', error);
    } finally {
      setForm(form);
    }
  }, [form, setOperatorDropdownList, setWorkstationDropdownList, operatorDropdownListMap, workstationDropdownListMap, setForm]);

  React.useLayoutEffect(() => {
    getCache();
  }, [getCache]);

  React.useEffect(() => {
    const cache = getWorkstationDashboardCache(CacheType.OperatorPerformanceLast10Hours);
    if (cache) {
      form.setFieldsValue(cache);
    }
  }, [form]);

  const tableColumns = React.useMemo(() => {
    const hourItems = timeIntervals?.reduce((acc, curr) => {
      acc.unshift(curr);
      return acc;
    }, [] as string[]);
    return [
      {
        title: $gt('Operator'),
        dataIndex: 'operator',
        ellipsis: true,
        width: 150,
        lineClamp: 1
      },
      {
        title: $gt('Workstation'),
        dataIndex: 'workstation',
        ellipsis: true,
        width: 150,
        lineClamp: 1
      },
      {
        title: $gt('Throughput/Target'),
        align: 'center',
        key: 'hour_items',
        dataIndex: 'hour_items',
        children: hourItems.map((item, index) => {
          const { startHour } = extractHourAndSuffix(item);
          return {
            title: item,
            key: startHour,
            dataIndex: startHour,
            sorter: {
              key: index,
              value: sorterModel[index],
              directions: ['ascend', 'descend'],
            },
            render: (_: any, row: OperatorPerformanceLastTenHoursItem, index: number) => {
              const target = row.hour_items?.find(item => getStartHourBySecond(item.time_begin) === startHour);
              if (!target) return <span>-</span>;
              const showException = (target.throughput ?? 0) < (target.target ?? 0);
              const isEmpty = !target.throughput && !target.target;
              return (
                <div className={cls(styles.throughputTarget, { [styles.warn]: showException })}>
                  {isEmpty ? (
                    <span>-</span>
                  ) : (
                    <>
                      <span className={cls(styles.throughput, { [styles.warn]: showException })}>{target.throughput}</span>
                      <span className={cls(styles.target, { [styles.warn]: showException })}>/{target.target}</span>
                    </>
                  )}
                  {showException && <ExceptionIcon />}
                </div>
              );
            }
          };
        })
      },
    ] as ColumnProps<OperatorPerformanceLastTenHoursItem>[];
  }, [sorterModel, timeIntervals]);

  const handleTableChange = React.useCallback((pagination, filters, sorter) => {
    const field = (sorter as SorterResult<Record<string, any>>).field;
    const order = (sorter as SorterResult<Record<string, any>>).order;
    const newModel = {
      ...defaultSortModel,
      [field as string]: order
    };
    sorterModelRef.current = newModel;
    setSorterModel(newModel);
    setPagination({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || DEFAULT_PAGE_SIZE,
      total: pagination.total || 0,
    });
    getOperatorPerformanceLastTenHoursAsync({ workstation_group_id: targetGroup?.groupId });
  }, [setSorterModel, setPagination, getOperatorPerformanceLastTenHoursAsync, targetGroup?.groupId]);

  return (
    <div>
      <div className={styles.subTitle}>
        {$gt('Operator Performance')}
      </div>
      <ProFilter
        formProps={{ form }}
        columns={3}
        fields={[
          {
            label: $gt('Workstation ID/Name'),
            type: 'select',
            name: 'biz_workstation_id_list',
            ctrlProps: {
              mode: 'multiple',
              maxTagCount: 1,
              maxTagTextWidth: 80,
              optionLineClamp: 1,
              useTooltip: true,
              filterOption: true,
              allowClear: true,
              loading: workstationDropdownListLoading,
              options: workstationDropdownList
            }
          },
          {
            label: $gt('Operator ID/Name'),
            type: 'select',
            name: 'operator_id_list',
            ctrlProps: {
              mode: 'multiple',
              maxTagCount: 1,
              maxTagTextWidth: 80,
              optionLineClamp: 1,
              filterOption: true,
              allowClear: true,
              useTooltip: true,
              loading: operatorDropdownListLoading,
              options: operatorDropdownList
            }
          },
        ]}
        onChange={model => {
          if (!targetGroup) return;
          setCache(model);
          getOperatorPerformanceLastTenHoursAsync({ workstation_group_id: targetGroup?.groupId });
        }}
      />
      <Table
        bordered
        dataSource={items}
        loading={loading}
        columns={tableColumns}
        pagination={pagination}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default OperatorPerformanceLastTenHours;
